import { GoogleGenerativeAI } from '@google/generative-ai';
import { BoundingBox } from './imageProcessing';
import Constants from 'expo-constants';
import { Database } from '../lib/newDatabase.types';
import type { BackendCatalogData } from '../types/newDatabaseTypes';
import { hashImageData, getCachedResult, setCachedResult } from './geminiCache';

const API_KEY = Constants.expoConfig?.extra?.EXPO_PUBLIC_GEMINI_API_KEY;

if (!API_KEY) {
  console.warn('No Gemini API key found in environment variables');
}

const genAI = new GoogleGenerativeAI(API_KEY || '');

// Define the type for the condition based on the generated Supabase enum
type ItemCondition = Database['public']['Enums']['item_condition'];

export interface AnalyzedItem {
  name: string;
  brand?: string;
  model?: string;
  description?: string;
  confidenceScore: number;
  estimatedValue?: number | null;
  priceRange?: { 
    min: number;
    max: number;
  } | null; // Allow null/undefined if AI doesn't provide it
  condition?: ItemCondition | null; // Use the enum type, allow null
  conditionScore?: number | null; // Add the new condition score field
  category?: string;
  demandScore?: number;
  missingInfo?: string[];
  lendingSuitability?: number;
  purchaseDate?: string;
  // Keep originalValue name, maps to DB purchase_price
  originalValue?: number | null; 
  depreciationRate?: number;
  boundingBox?: BoundingBox;
  selected?: boolean;
  image_url?: string;
  family?: string;
  variantFields?: Array<{ name: string; value: string }>; // Gemini-detected variant attributes
  // Backend-specific data (when using backend API)
  _backendData?: BackendCatalogData;
}

function getUserFriendlyError(error: any): string {
  if (error?.message?.includes('token count') && error?.message?.includes('exceeds the maximum')) {
    return 'The image is too large. Please try using a smaller image or reducing its quality.';
  }
  if (error?.message?.includes('400')) {
    return 'Unable to analyze the image. Please try a different image or ensure it\'s in a supported format (JPEG, PNG).';
  }
  if (error?.message?.includes('403')) {
    return 'Unable to access the image analysis service. Please try again later.';
  }
  return 'An error occurred while analyzing the image. Please try again.';
}

// Helper function to normalize and validate the condition string from AI
// Export the function so it can be used elsewhere
export function normalizeCondition(aiConditionString?: string | null): ItemCondition | null {
  if (!aiConditionString) {
    return null; // No condition provided
  }

  const normalized = aiConditionString.trim().toLowerCase();

  switch (normalized) {
    case 'new':
      return 'New';
    case 'like new':
      return 'Like New';
    case 'excellent':
      return 'Excellent';
    case 'good':
      return 'Good';
    case 'fair':
      return 'Fair';
    case 'poor':
      return 'Poor';
    // Optional: Add fuzzy matching for common variations
    case 'very good': 
    case 'great condition':
         return 'Excellent'; // Example mapping
    case 'brand new':
    case 'new with tags':
         return 'New';
    // --- Add other mappings as needed ---
    default:
      console.warn(`Unexpected condition value from AI: "${aiConditionString}". Setting to null.`);
      return null; // Return null for unmapped/unexpected values
  }
}

// Analyze video by treating it as a sequence of frames
// Note: For now, we'll use the existing multi-image analysis since
// direct video upload requires server-side File API which isn't available in React Native
export async function analyzeVideoFrames(frameBase64Images: string[]): Promise<AnalyzedItem[]> {
  try {
    console.log(`Starting Gemini video frame analysis with ${frameBase64Images.length} frames...`);
    
    // Use the existing multi-image analysis but with video-specific prompt
    const model = genAI.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: { temperature: 0 }
    });
    
    const prompt = `
Analyze these ${frameBase64Images.length} frames extracted from a video showing one or more consumer products. Use all frames together to provide a comprehensive analysis.

Identify ALL consumer products visible across these video frames. Focus only on resellable consumer products, ignoring accessories, surfaces, or background elements.

For EACH item detected, provide:
1. The item name (brief, generic name like "Sneakers" or "Laptop")
2. Brand (specific manufacturer like "Nike" or "Apple")
3. Model (specific product line like "Air Max 90" or "MacBook Pro M2")
4. A comprehensive description including color, size, and notable features visible throughout the video
5. A confidence score between 0 and 1 for the identification (higher confidence due to video capture)
6. Estimated resale price range (min and max)
7. Condition grade (new, like new, excellent, good, fair, poor) - assess from all visible angles
8. Category tag for the item type
9. Demand score (0-1) indicating market interest
10. Key missing info needed for a complete listing
11. Lending suitability score (0-1)
12. Estimated purchase date (YYYY-MM)
13. Original estimated value
14. Annual price depreciation rate (decimal)
15. Condition score: A numeric value between 0.00 (very poor condition) and 1.00 (brand new), based on the visible physical state throughout the video

Format the response as a JSON array containing one object per detected product:
[
  {
    "name": string,          // Generic product name
    "brand": string,         // Manufacturer/brand name
    "model": string,         // Specific model/product line
    "description": string,   // Comprehensive description from video
    "confidence": number,    // Confidence score between 0 and 1
    "priceRange": { "min": number, "max": number },
    "condition": string,
    "category": string,
    "demandScore": number,
    "missingInfo": string[],
    "lendingScore": number,
    "purchaseDate": string,
    "originalValue": number,
    "depreciationRate": number,
    "conditionScore": number
  }
]

If multiple products are visible in the video, return multiple objects in the array. If only one product is visible, return an array with one object.`;

    // Create parts array for Gemini API
    const parts: any[] = [{ text: prompt }];
    
    // Add all frame images to the request
    frameBase64Images.forEach((img, index) => {
      console.log(`Adding frame ${index + 1} to request (base64 length: ${img.length})`);
      parts.push({
        inlineData: {
          mimeType: 'image/jpeg',
          data: img
        }
      });
    });
    
    console.log('Sending video frames to Gemini API...');
    const result = await model.generateContent(parts);
    console.log('Received response from Gemini API');
    
    const response = await result.response;
    console.log('Processing Gemini response...');
    const text = response.text();
    console.log('Raw response:', text);
    
    try {
      // Extract JSON from markdown code blocks if present
      const jsonMatch = text.match(/```(?:json)?\n([\s\S]*?)\n```/) || text.match(/```([\s\S]*?)```/);
      const jsonString = jsonMatch ? jsonMatch[1].trim() : text.trim();
      console.log('Extracted JSON string:', jsonString);
      
      const items: any[] = JSON.parse(jsonString);
      
      return items.map((item): AnalyzedItem => {
        const isValidPriceRange = (
          typeof item.priceRange?.min === 'number' && 
          typeof item.priceRange?.max === 'number'
        );

        const mappedItem: AnalyzedItem = {
          name: item.name || 'Unknown Item',
          brand: item.brand,
          model: item.model,
          description: item.description,
          confidenceScore: typeof item.confidence === 'number' ? item.confidence : (typeof item.confidenceScore === 'number' ? item.confidenceScore : 0),
          priceRange: isValidPriceRange ? { min: item.priceRange.min, max: item.priceRange.max } : undefined,
          condition: normalizeCondition(item.condition),
          conditionScore: typeof item.conditionScore === 'number' ? item.conditionScore : undefined,
          category: item.category,
          demandScore: typeof item.demandScore === 'number' ? item.demandScore : undefined,
          missingInfo: item.missingInfo,
          lendingSuitability: typeof item.lendingSuitability === 'number' ? item.lendingSuitability : undefined,
          purchaseDate: item.purchaseDate,
          originalValue: typeof item.originalValue === 'number' ? item.originalValue : undefined,
          depreciationRate: typeof item.depreciationRate === 'number' ? item.depreciationRate : undefined,
        };
        return mappedItem;
      });
    } catch (error: unknown) {
      console.error('Error parsing Gemini video response:', error);
      console.error('Raw text that failed to parse:', text);
      throw new Error('Unable to understand the video analysis results. Please try again.');
    }
  } catch (error) {
    console.error('Error analyzing video frames:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    }
    throw new Error(getUserFriendlyError(error));
  }
}

export async function analyzeImage(base64Image: string | string[]): Promise<AnalyzedItem[]> {
  try {
    // Check cache first
    const imageHash = hashImageData(base64Image);
    const cachedResult = await getCachedResult(imageHash);
    if (cachedResult) {
      console.log('Returning cached Gemini result');
      return cachedResult;
    }
    
    console.log('Starting Gemini API call...');
    
    const model = genAI.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: { temperature: 0 }
    });
    
    // Check if we're analyzing multiple images of the same item
    const isMultiImage = Array.isArray(base64Image);
    const imageCount = isMultiImage ? base64Image.length : 1;
    
    console.log(`Analyzing ${imageCount} image(s)`);
    
    const prompt = isMultiImage 
      ? `
    Analyze these ${imageCount} images which show different angles/views of the SAME product. Use all images together to provide a comprehensive analysis.
    
    Identify the consumer product shown across these images. Focus only on the main product, ignoring accessories, surfaces, or background elements.
    
    For the item, provide:
     1. The item name (brief, generic name like "Sneakers" or "Laptop")
    2. Brand (specific manufacturer like "Nike" or "Apple")
    3. Model (specific product line like "Air Max 90" or "MacBook Pro M2")
    4. A comprehensive description including color, size, and notable features visible across all images
    5. A confidence score between 0 and 1 for the identification (higher confidence due to multiple angles)
    6. Estimated resale price range (min and max) based on current secondhand market prices for this specific brand/model in the assessed condition. The range should reflect realistic price variance (typically 15-25% spread), not the full spectrum of possible prices.
    7. Condition grade (new, like new, excellent, good, fair, poor) - assess from all visible angles
    8. Category tag for the item type
    9. Demand score (0-1) indicating market interest
    10. Key missing info needed for a complete listing
    11. Lending suitability score (0-1)
    12. Estimated purchase date (YYYY-MM)
    13. Original estimated value
    14. Annual price depreciation rate (decimal)
    15. Condition score: A numeric value between 0.00 (very poor condition) and 1.00 (brand new), based on the visible physical state across all images

    Format the response as a JSON array with a single object containing:
    {
      "name": string,          // Generic product name
      "brand": string,         // Manufacturer/brand name
      "model": string,         // Specific model/product line
      "description": string,   // Comprehensive description from all angles
      "confidence": number,    // Confidence score between 0 and 1
      "priceRange": { "min": number, "max": number },
      "condition": string,
      "category": string,
      "demandScore": number,
      "missingInfo": string[],
      "lendingScore": number,
      "purchaseDate": string,
      "originalValue": number,
      "depreciationRate": number,
      "conditionScore": number
    }`
      : `
    Analyze this image and identify any consumer products or items that could be resold. Focus only on consumer products, ignoring accessories, surfaces, or background elements.
    For each item, provide:
     1. The item name (brief, generic name like "Sneakers" or "Laptop")
    2. Brand (specific manufacturer like "Nike" or "Apple")
    3. Model (specific product line like "Air Max 90" or "MacBook Pro M2")
    4. A brief description including color, size, and notable features
    5. A confidence score between 0 and 1 for the identification
    6. Estimated resale price range (min and max) based on current secondhand market prices for this specific brand/model in the assessed condition. The range should reflect realistic price variance (typically 15-25% spread), not the full spectrum of possible prices.
    7. Condition grade (new, like new, excellent, good, fair, poor)
    8. Category tag for the item type
    9. Demand score (0-1) indicating market interest
    10. Key missing info needed for a complete listing
    11. Lending suitability score (0-1)
    12. Estimated purchase date (YYYY-MM)
    13. Original estimated value
    14. Annual price depreciation rate (decimal)width and height dimensions
    15. Location in image (x, y coordinates and dimensions)
    16. Condition score: A numeric value between 0.00 (very poor condition) and 1.00 (brand new), based only on the visible physical state of the item.

    Format the response as a JSON array with a single object containing:
    {
      "name": string,          // Generic product name
      "brand": string,         // Manufacturer/brand name
      "model": string,         // Specific model/product line
      "description": string,   // Brief description of the item
      "confidence": number,    // Confidence score between 0 and 1
      "priceRange": { "min": number, "max": number },
      "condition": string,
      "category": string,
      "demandScore": number,
      "missingInfo": string[],
      "lendingScore": number,
      "purchaseDate": string,
      "originalValue": number,
      "depreciationRate": number,
      "boundingBox": { "x": number, "y": number, "width": number, "height": number },
      "conditionScore": number
    }`;

    // Create parts array for Gemini API
    const parts: any[] = [{ text: prompt }];
    
    // Add all images to the request
    if (isMultiImage) {
      base64Image.forEach((img, index) => {
        console.log(`Adding image ${index + 1} to request (base64 length: ${img.length})`);
        parts.push({
          inlineData: {
            mimeType: 'image/jpeg',
            data: img
          }
        });
      });
    } else {
      console.log('Received base64 length in analyzeImage:', base64Image.length);
      parts.push({
        inlineData: {
          mimeType: 'image/jpeg',
          data: base64Image
        }
      });
    }
    
    console.log('Sending request to Gemini API...');
    const result = await model.generateContent(parts);
    console.log('Received response from Gemini API');
    
    const response = await result.response;
    console.log('Processing Gemini response...');
    const text = response.text();
    console.log('Raw response:', text);
    
    try {
      // Extract JSON from markdown code blocks if present
      const jsonMatch = text.match(/```(?:json)?\n([\s\S]*?)\n```/) || text.match(/```([\s\S]*?)```/);
      const jsonString = jsonMatch ? jsonMatch[1].trim() : text.trim();
      console.log('Extracted JSON string:', jsonString);
      
      const items: any[] = JSON.parse(jsonString); // Use 'any' carefully, refine if possible
      
      const result = items.map((item, index): AnalyzedItem => {
        // Helper to check if priceRange is valid
        const isValidPriceRange = (
          typeof item.priceRange?.min === 'number' && 
          typeof item.priceRange?.max === 'number'
        );

        const mappedItem: AnalyzedItem = {
          name: item.name || 'Unknown Item',
          brand: item.brand,
          model: item.model,
          description: item.description,
          confidenceScore: typeof item.confidence === 'number' ? item.confidence : (typeof item.confidenceScore === 'number' ? item.confidenceScore : 0),
          // Assign the nested priceRange object directly if valid
          priceRange: isValidPriceRange ? { min: item.priceRange.min, max: item.priceRange.max } : undefined,
          condition: normalizeCondition(item.condition),
          conditionScore: typeof item.conditionScore === 'number' ? item.conditionScore : undefined,
          category: item.category,
          demandScore: typeof item.demandScore === 'number' ? item.demandScore : undefined,
          missingInfo: item.missingInfo,
          lendingSuitability: typeof item.lendingSuitability === 'number' ? item.lendingSuitability : undefined,
          purchaseDate: item.purchaseDate,
          // Map item.originalValue to interface originalValue
          originalValue: typeof item.originalValue === 'number' ? item.originalValue : undefined,
          depreciationRate: typeof item.depreciationRate === 'number' ? item.depreciationRate : undefined,
          // location: item.location, // Assuming location parsing might be complex
        };
        return mappedItem;
      });
      
      // Cache the result for future requests
      await setCachedResult(imageHash, result);
      
      return result;

    } catch (error: unknown) {
      console.error('Error parsing Gemini response:', error);
      console.error('Raw text that failed to parse:', text);
      throw new Error('Unable to understand the analysis results. Please try again.');
    }
  } catch (error) {
    console.error('Error analyzing image:', error);
    if (error instanceof Error) {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    }
    throw new Error(getUserFriendlyError(error));
  }
}
